import React, { useState } from 'react';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const FlagsSymbols = () => {
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const flagsAndSymbols = [
    '🏁', '🚩', '🎌', '🏴', '🏳️', '🏳️‍🌈', '🏳️‍⚧️', '🏴‍☠️',
    '⚡', '⭐', '✨', '💫', '☀️', '🌟', '⚜️', '🔱',
    '📛', '🔰', '⚠️', '☢️', '☣️', '🆚', '🈁', '🈂️',
    '🈷️', '🈶', '🈯', '🉐', '🈹', '🈚', '🈲', '🉑',
    '🈸', '🈴', '🈳', '㊗️', '㊙️', '🈺', '🈵', '▶️',
    '⏩', '⏭️', '⏯️', '◀️', '⏪', '⏮️', '🔼', '⏫',
    '🔽', '⏬', '⏸️', '⏹️', '⏺️', '⏏️', '🎦', '🔅'
  ].join('');

  const handleCopy = () => {
    navigator.clipboard.writeText(flagsAndSymbols);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const title = `Flags & Symbols Emojis 🏁 Copy and Paste [${currentYear}] ⚡`;
  const description = `【INSTANT COPY】 Get all flags & symbols emojis! 🏁 Perfect collection of flags and special symbols ⚡ One-click copy & paste ✨ Works everywhere! Updated for ${currentYear}`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href="https://emojicollection.net/emoji/flags-symbols" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://emojicollection.net/emoji/flags-symbols" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="keywords" content="flags emojis, symbol emojis, flag emoji copy paste, special symbols, emoji symbols, instagram flags, whatsapp symbols" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </button>

          <article className="bg-white rounded-2xl shadow-lg p-8">
            <header className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-500 mb-4">
                <span className="text-2xl">🏁</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Flags & Symbols Emojis 🏁 Copy and Paste [{currentYear}]
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Get instant access to a complete collection of flag and symbol emojis! Perfect for adding visual elements to your messages and content. Updated for {currentYear} with the latest emojis. One-click copy and paste feature!
              </p>
            </header>

            <section className="max-w-2xl mx-auto mb-12">
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                  🏁 Copy Flags & Symbols Instantly
                </h2>
                <div className="mb-4 overflow-auto max-h-96 text-left break-words text-2xl">
                  {flagsAndSymbols}
                </div>
                <button
                  onClick={handleCopy}
                  className={`inline-flex items-center px-6 py-3 rounded-lg text-lg font-medium transition-all transform hover:scale-105 ${
                    copied
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600'
                  }`}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2" size={20} />
                      Copied All Symbols!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2" size={20} />
                      Copy All Symbols
                    </>
                  )}
                </button>
              </div>
            </section>

            <section className="border-t border-gray-200 pt-8">
              <h2 className="text-2xl font-semibold mb-4">✨ Features & Benefits</h2>
              <div className="text-gray-600 space-y-4">
                <p>🏁 Complete collection of flags & symbols</p>
                <p>⚡ Perfect for visual communication</p>
                <p>📋 Instant one-click copy functionality</p>
                <p>💫 Works on all platforms and devices</p>
                <p>🆕 Updated for {currentYear} with latest emojis</p>
                <p>🔄 Includes special symbols and indicators</p>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">📱 Perfect For</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="text-lg font-medium mb-2">Communication</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>International messages</li>
                    <li>Visual indicators</li>
                    <li>Special symbols</li>
                    <li>Status updates</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Social Media</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>Instagram posts</li>
                    <li>Facebook updates</li>
                    <li>Twitter messages</li>
                    <li>TikTok captions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2">Messaging</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600">
                    <li>WhatsApp</li>
                    <li>Messenger</li>
                    <li>iMessage</li>
                    <li>SMS</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="border-t border-gray-200 mt-8 pt-8">
              <h2 className="text-2xl font-semibold mb-4">❓ Frequently Asked Questions</h2>
              <div className="space-y-4">
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">What are flag and symbol emojis used for?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Flag and symbol emojis are used to represent countries, causes, and special indicators in digital communication. They help convey information visually and add meaning to messages.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Do these symbols work everywhere?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Yes! These emojis are universal and work on all major platforms including Instagram, Facebook, WhatsApp, and more. They're compatible with both mobile and desktop devices.
                  </div>
                </details>
                <details className="group bg-gray-50 rounded-lg">
                  <summary className="cursor-pointer p-4 font-medium">Are these emojis free to use?</summary>
                  <div className="p-4 pt-0 text-gray-600">
                    Absolutely! Our flag and symbol emojis are completely free to use for both personal and commercial purposes. No attribution required.
                  </div>
                </details>
              </div>
            </section>
          </article>
        </div>
      </div>
    </>
  );
};

export default FlagsSymbols;